import { useTranslation } from "react-i18next";
import {
  GoMakeAutoComplate,
  GoMakeDeleteModal,
  GoMakeModal,
  GomakePrimaryButton,
  GomakeTextInput,
} from "@/components";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { PlusIcon, ReOrderIcon, RemoveIcon } from "@/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useSnackBar } from "@/hooks";
import { useMaterials } from "@/hooks/use-materials";
import { ModalType } from "@/enums";
import { useModal } from "@/hooks/use-modal";
import {
  applyColorPresetSelection,
  buildColorCountOptions,
  COLOR_PRESET_TYPE_OPTIONS,
  ColorCatalogItem,
  ColorPresetTypeId,
  ensureSelectedParameterValues,
  findSettingParamByCodes,
  getColorCatalogFromMaterials,
  getColorCountFromConfig,
  getSelectedColorCatalogItems,
  inferPresetTypeFromConfig,
  inferPresetTypeFromUpdateName,
  normalizeMaterialsList,
  COLOR_DETAIL_CODES,
} from "./printing-colors-preset-utils";
import { useStyle } from "@/widgets/shared-admin-customers/add-product/child-parameter-modal/style";
import { TrashIcon } from "@/components/icons/trash-icon";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";

const PrintingColorsOptionsModal = ({
  openModal,
  onClose,
  modalTitle,
  selectedParameter,
  updatedValuesConfigsForParameters,
  selectedSectonId,
  selectedSubSection,
}: any) => {
  const { t } = useTranslation();
  const { clasess } = useStyle();
  const { setSnackbarStateValue } = useSnackBar();
  const { allMaterials, getAllMaterial } = useMaterials();
  const openDeleteModal = useModal();
  const [valuesConfigs, setValuesConfigs] = useState<any[]>([]);
  const initializedRef = useRef(false);
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [pendingScrollRowId, setPendingScrollRowId] = useState<string | null>(
    null
  );
  const { errorColor } = useGomakeTheme();

  const settingParameters = selectedParameter?.settingParameters ?? [];
  const colorCountOptions = useMemo(() => buildColorCountOptions(9), []);
  const materialsList = useMemo(
    () => normalizeMaterialsList(allMaterials),
    [allMaterials]
  );
  const colorCatalog = useMemo(
    () => getColorCatalogFromMaterials(materialsList),
    [materialsList]
  );

  useEffect(() => {
    if (openModal) {
      void getAllMaterial();
    }
  }, [openModal]);

  useEffect(() => {
    if (!openModal) {
      initializedRef.current = false;
      return;
    }
    if (
      initializedRef.current ||
      !selectedParameter?.valuesConfigs ||
      materialsList.length === 0
    ) {
      return;
    }
    initializedRef.current = true;
    setValuesConfigs(
      selectedParameter.valuesConfigs.map((item: any) => {
        const ensured = ensureSelectedParameterValues(
          { ...item },
          settingParameters
        );
        const presetType =
          item.colorPresetType ??
          inferPresetTypeFromConfig(ensured, settingParameters, materialsList);
        const count =
          item.colorPresetCount ??
          getColorCountFromConfig(ensured, settingParameters);
        const customColorIds =
          presetType === "custom"
            ? getSelectedColorCatalogItems(
              ensured,
              settingParameters,
              materialsList
            ).map((c) => c.valueId)
            : undefined;
        return applyColorPresetSelection(
          ensured,
          presetType,
          count,
          settingParameters,
          materialsList,
          customColorIds
        );
      })
    );
  }, [openModal, selectedParameter, materialsList, settingParameters]);

  useEffect(() => {
    if (!pendingScrollRowId) {
      return;
    }

    const scrollToAddedRow = () => {
      const rowEl = rowRefs.current[pendingScrollRowId];
      if (rowEl) {
        rowEl.scrollIntoView({ behavior: "smooth", block: "end" });
        setPendingScrollRowId(null);
      }
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(scrollToAddedRow);
    });
  }, [valuesConfigs, pendingScrollRowId]);

  const isUpdateNameRepeated = (updateName: string, currentIndex: number) => {
    const formatted = (updateName ?? "").replace(/\s/g, "").toLowerCase();
    if (!formatted) return false;
    return valuesConfigs.some(
      (item, index) =>
        !item?.isDeleted &&
        index !== currentIndex &&
        (item.updateName ?? "").replace(/\s/g, "").toLowerCase() === formatted
    );
  };

  const checkUpdateNames = (array: any[]) =>
    array
      .filter((item) => !item?.isDeleted)
      .every((item) => item.updateName?.trim());

  const getCustomColorIdsFromConfig = (config: any): string[] => {
    const printingcolorsDetail = findSettingParamByCodes(
      settingParameters,
      COLOR_DETAIL_CODES
    );
    const colorEntry = config?.selectedParameterValues?.find(
      (p: any) => p.parameterId === printingcolorsDetail?.id
    );
    return colorEntry?.valueIds ?? [];
  };

  const validateCustomRows = (): boolean => {
    for (const item of valuesConfigs) {
      if (item?.isDeleted) continue;

      const ensured = ensureSelectedParameterValues(item, settingParameters);
      const colorCount = getColorCountFromConfig(ensured, settingParameters);
      const selectedIds = getCustomColorIdsFromConfig(ensured);

      if (selectedIds.length === 0) continue;

      if (selectedIds.length !== colorCount) {
        setSnackbarStateValue({
          state: true,
          message: t("products.offsetPrice.admin.selectColorsForCustom"),
          type: "error",
        });
        return false;
      }
    }
    return true;
  };

  const changeItems = (index: number, fieldName: string, value: any) => {
    setValuesConfigs((prev) => {
      const temp = [...prev];
      temp[index] = { ...temp[index], [fieldName]: value };
      return temp;
    });
  };

  const applyPresetAtIndex = (
    index: number,
    presetTypeId: ColorPresetTypeId,
    colorCount: number,
    customColorIds?: string[]
  ) => {
    setValuesConfigs((prev) => {
      const temp = [...prev];
      temp[index] = applyColorPresetSelection(
        temp[index],
        presetTypeId,
        colorCount,
        settingParameters,
        materialsList,
        customColorIds
      );
      return temp;
    });
  };

  const applyCustomColorsAtIndex = (
    index: number,
    selectedColors: ColorCatalogItem[]
  ) => {
    setValuesConfigs((prev) => {
      const item = prev[index];
      const colorCount =
        item?.colorPresetCount ??
        getColorCountFromConfig(item, settingParameters);
      const capped = selectedColors.slice(0, colorCount);
      const temp = [...prev];
      temp[index] = applyColorPresetSelection(
        temp[index],
        "custom",
        colorCount,
        settingParameters,
        materialsList,
        capped.map((c) => c.valueId)
      );
      return temp;
    });
  };

  const reorder = (list: any[], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) return;
    setValuesConfigs(
      reorder(valuesConfigs, result.source.index, result.destination.index)
    );
  };

  const deleteRow = (selectedRow: any) => {
    if (selectedRow?.isDeletable === false) return;
    setValuesConfigs((prev) =>
      prev.filter((obj) => obj?.id !== selectedRow?.id)
    );
  };

  const getItemStyle = (isDragging: boolean, draggableStyle: any) => ({
    padding: 10,
    margin: `0 0 8px 0`,
    userSelect: "none" as const,
    display: "flex",
    flexDirection: "row" as const,
    alignItems: "center",
    width: "100%",
    gap: 12,
    flexWrap: "wrap" as const,
    backgroundColor: isDragging ? "#FAFAFA" : "",
    ...draggableStyle,
    top: 0,
    left: 0,
    position: "relative" as const,
  });

  const handleSave = () => {
    const activeConfigs = valuesConfigs.filter((item) => !item?.isDeleted);
    const hasRepeated = activeConfigs.some((item, index) =>
      isUpdateNameRepeated(
        item?.updateName,
        valuesConfigs.findIndex((row) => row.id === item.id)
      )
    );
    const hasNames = checkUpdateNames(valuesConfigs);
    const customRowsValid = validateCustomRows();

    if (!hasNames) {
      setSnackbarStateValue({
        state: true,
        message: t("products.offsetPrice.admin.fillValue"),
        type: "error",
      });
      return;
    }
    if (hasRepeated) {
      setSnackbarStateValue({
        state: true,
        message: t("products.offsetPrice.admin.valuesErrorMsg"),
        type: "error",
      });
      return;
    }
    if (!customRowsValid) {
      return;
    }

    const payloadConfigs = valuesConfigs.map(
      ({ colorPresetType, colorPresetCount, ...rest }) =>
        ensureSelectedParameterValues(rest, settingParameters)
    );
    updatedValuesConfigsForParameters(selectedSectonId, selectedSubSection, {
      ...selectedParameter,
      valuesConfigs: payloadConfigs,
    });
    onClose();
  };

  const addNewRow = () => {
    const newRowId = uuidv4();
    setPendingScrollRowId(newRowId);
    setValuesConfigs((prev) => [
      ...prev,
      {
        ...applyColorPresetSelection(
          ensureSelectedParameterValues(
            {
              id: newRowId,
              isHidden: false,
              isDefault: prev.length === 0,
              isDeleted: false,
              isDeletable: true,
              updateName: "",
              materialValueIds: [],
              selectedParameterValues: null,
            },
            settingParameters
          ),
          "custom",
          1,
          settingParameters,
          materialsList
        ),
        updateName: "",
      },
    ]);
  };

  return (
    <>
      <GoMakeModal
        openModal={openModal}
        modalTitle={modalTitle}
        onClose={onClose}
        insideStyle={{ ...clasess.insideStyle, width: "70%", minWidth: 640, minHeight: 700 }}
      >
        <div>
          <div style={{ height: 550, overflow: "auto" }}>
            <div style={clasess.addBtnStyle}>
              <div style={{ cursor: "pointer" }} onClick={addNewRow}>
                <PlusIcon width={25} height={25} />
              </div>
            </div>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="printing-colors-presets">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {valuesConfigs.map((item, index) => {
                      const presetType: ColorPresetTypeId =
                        inferPresetTypeFromUpdateName(item?.updateName ?? "") ??
                        item.colorPresetType ??
                        inferPresetTypeFromConfig(
                          item,
                          settingParameters,
                          materialsList
                        );
                      const isCustom = presetType === "custom";
                      const presetMeta = COLOR_PRESET_TYPE_OPTIONS.find(
                        (p) => p.id === presetType
                      );
                      const isFixedPreset = presetMeta?.fixedCount != null;
                      const colorCount =
                        item.colorPresetCount ??
                        getColorCountFromConfig(item, settingParameters);
                      const selectedColors = getSelectedColorCatalogItems(
                        item,
                        settingParameters,
                        materialsList
                      );

                      return (
                        <Draggable
                          key={item.id}
                          draggableId={item.id}
                          index={index}
                        >
                          {(dragProvided, snapshot) => (
                            <div
                              ref={(el) => {
                                dragProvided.innerRef(el);
                                rowRefs.current[item.id] = el;
                              }}
                              {...dragProvided.draggableProps}
                              {...dragProvided.dragHandleProps}
                              style={getItemStyle(
                                snapshot.isDragging,
                                dragProvided.draggableProps.style
                              )}
                            >
                              <ReOrderIcon />
                              <div
                                style={{
                                  ...clasess.textInputContainer,
                                  flex: 1,
                                  minWidth: 140,
                                }}
                              >
                                <GomakeTextInput
                                  style={clasess.textInputStyle}
                                  placeholder={t(
                                    "products.profits.exceptions.enterValue"
                                  )}
                                  disabled={isFixedPreset}
                                  value={item?.updateName ?? ""}
                                  onChange={(e) =>
                                    changeItems(
                                      index,
                                      "updateName",
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                              <div
                                style={{
                                  ...clasess.textInputContainer,
                                  minWidth: 120,
                                }}
                              >
                                <GoMakeAutoComplate
                                  options={colorCountOptions}
                                  disabled={isFixedPreset}
                                  placeholder={t(
                                    "products.offsetPrice.admin.colorCount",
                                    { defaultValue: "Number of colors" }
                                  )}
                                  style={clasess.dropDownListStyle}
                                  getOptionLabel={(option: any) => option.label}
                                  value={
                                    colorCountOptions.find(
                                      (c) => c.id === colorCount
                                    ) ?? null
                                  }
                                  onChange={(_e: any, option: any) => {
                                    if (!option || isFixedPreset) return;
                                    const existingIds = getCustomColorIdsFromConfig(
                                      item
                                    ).slice(0, option.id);
                                    applyPresetAtIndex(
                                      index,
                                      "custom",
                                      option.id,
                                      existingIds
                                    );
                                  }}
                                />
                              </div>

                              <div
                                style={{
                                  ...clasess.textInputContainer,
                                  flex: 2,
                                  minWidth: 220,
                                }}
                              >
                                <GoMakeAutoComplate
                                  multiple
                                  disabled={!isCustom}
                                  options={colorCatalog}
                                  placeholder={t(
                                    "products.offsetPrice.admin.selectColors",
                                    { defaultValue: "Select colors" }
                                  )}
                                  style={clasess.dropDownListStyle}
                                  getOptionLabel={(option: ColorCatalogItem) =>
                                    option.value
                                  }
                                  isOptionEqualToValue={(
                                    option: ColorCatalogItem,
                                    value: ColorCatalogItem
                                  ) => option.valueId === value.valueId}
                                  value={selectedColors}
                                  onChange={(_e: any, value: ColorCatalogItem[]) => {
                                    if (!isCustom) return;
                                    const next = Array.isArray(value)
                                      ? value
                                      : value
                                        ? [value]
                                        : [];
                                    applyCustomColorsAtIndex(index, next);
                                  }}
                                />
                              </div>
                              {item?.isDeletable !== false && (
                                <div
                                  style={{ cursor: "pointer" }}
                                  onClick={() => deleteRow(item)}
                                >
                                  <TrashIcon color={errorColor(500)} height={20} width={20} />
                                </div>
                              )}
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
          <div
            style={{
              marginTop: 10,
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <GomakePrimaryButton
              style={{ width: "50%", height: 40 }}
              onClick={() => openDeleteModal.open()}
            >
              {t("products.offsetPrice.admin.addValues")}
            </GomakePrimaryButton>
          </div>
        </div>
      </GoMakeModal>
      <GoMakeDeleteModal
        openModal={openDeleteModal.isOpen}
        onClose={() => openDeleteModal.close()}
        modalType={ModalType.WARNING}
        title={t("products.offsetPrice.admin.addValuesTitle")}
        subTitle={t("products.offsetPrice.admin.addValuesSubTitle")}
        onClickDelete={() => {
          openDeleteModal.close();
          handleSave();
        }}
      />
    </>
  );
};

export { PrintingColorsOptionsModal };
